import type {
	TableCardConfig,
	TableFeaturesConfig,
} from '../models/table-config.model';
import { JeptoTableCardDefaults } from '../components/cards/jepto-table-card/jepto-table-card.defaults';
import { parseChartVisualizationInput } from './parse-chart-visualization-input.util';

/** Editor-resolved Grid Pro rendering toggles (Table tab → `visualization.rendering`). */
export interface TableRenderingPresentation {
	/** When true, long cell text wraps and rows may grow (`strictHeights: false`). */
	wrapCellText: boolean;
	/** When true, users can drag column borders to resize. */
	columnResizingEnabled: boolean;
}

const DEFAULT_PAGE_SIZE = 10;

export function visualizationRecord(visualization: unknown): Record<string, unknown> {
	const parsed = parseChartVisualizationInput(visualization);
	if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
		return { ...(parsed as Record<string, unknown>) };
	}
	return {};
}

function deepMergeRecord(
	base: Record<string, unknown>,
	incoming: Record<string, unknown>
): Record<string, unknown> {
	const result: Record<string, unknown> = { ...base };
	for (const key of Object.keys(incoming)) {
		const baseValue = result[key];
		const incomingValue = incoming[key];
		if (
			baseValue !== null &&
			typeof baseValue === 'object' &&
			!Array.isArray(baseValue) &&
			incomingValue !== null &&
			typeof incomingValue === 'object' &&
			!Array.isArray(incomingValue)
		) {
			result[key] = deepMergeRecord(
				baseValue as Record<string, unknown>,
				incomingValue as Record<string, unknown>
			);
		} else {
			result[key] = incomingValue;
		}
	}
	return result;
}

/**
 * Resolved page size for fetch/runtime (features win, then persisted visualization).
 */
export function tablePageSizeFromConfig(config: TableCardConfig): number {
	const fromFeatures = config.features?.pagination?.pageSize;
	if (
		typeof fromFeatures === 'number' &&
		Number.isFinite(fromFeatures) &&
		fromFeatures >= 1
	) {
		return fromFeatures;
	}
	const viz = parseChartVisualizationInput(config.visualization);
	if (viz && typeof viz === 'object' && !Array.isArray(viz)) {
		const pagination = (viz as Record<string, unknown>)['pagination'];
		if (
			pagination &&
			typeof pagination === 'object' &&
			!Array.isArray(pagination)
		) {
			const pageSize = (pagination as Record<string, unknown>)[
				'pageSize'
			];
			if (
				typeof pageSize === 'number' &&
				Number.isFinite(pageSize) &&
				pageSize >= 1
			) {
				return pageSize;
			}
		}
	}
	return DEFAULT_PAGE_SIZE;
}

/**
 * Reads presentation toggles from persisted `visualization` for editor hydration.
 */
export function featuresFromVisualization(
	visualization: unknown
): TableFeaturesConfig {
	const viz = visualizationRecord(visualization);
	const features: TableFeaturesConfig = {};

	const pagination = viz['pagination'];
	if (
		pagination &&
		typeof pagination === 'object' &&
		!Array.isArray(pagination)
	) {
		const p = pagination as Record<string, unknown>;
		features.pagination = {
			enabled:
				typeof p['enabled'] === 'boolean' ? p['enabled'] : true,
			pageSize:
				typeof p['pageSize'] === 'number' &&
				Number.isFinite(p['pageSize']) &&
				(p['pageSize'] as number) >= 1
					? (p['pageSize'] as number)
					: DEFAULT_PAGE_SIZE,
		};
	}

	const sorting = viz['sorting'];
	if (sorting && typeof sorting === 'object' && !Array.isArray(sorting)) {
		const s = sorting as Record<string, unknown>;
		features.sorting = {
			enabled:
				typeof s['enabled'] === 'boolean' ? s['enabled'] : true,
		};
	}

	return features;
}

/**
 * Merges table UI settings into `visualization` for `updateCard` (AppSync `AWSJSON`).
 * Column widths, order, pagination defaults, etc. belong here — not on `data`.
 */
export function mergeTableFeaturesIntoVisualization(
	visualization: unknown,
	features: TableFeaturesConfig | undefined
): Record<string, unknown> {
	const base = visualizationRecord(visualization);

	if (!features) {
		return base;
	}

	if (features.pagination !== undefined) {
		const existing =
			base['pagination'] &&
			typeof base['pagination'] === 'object' &&
			!Array.isArray(base['pagination'])
				? (base['pagination'] as Record<string, unknown>)
				: {};
		base['pagination'] = { ...existing, ...features.pagination };
	}

	if (features.sorting !== undefined) {
		const existing =
			base['sorting'] &&
			typeof base['sorting'] === 'object' &&
			!Array.isArray(base['sorting'])
				? (base['sorting'] as Record<string, unknown>)
				: {};
		base['sorting'] = { ...existing, ...features.sorting };
	}

	return base;
}

function renderingRecord(
	visualization: unknown
): Record<string, unknown> {
	const viz = visualizationRecord(visualization);
	const rendering = viz['rendering'];
	if (
		rendering &&
		typeof rendering === 'object' &&
		!Array.isArray(rendering)
	) {
		return rendering as Record<string, unknown>;
	}
	return {};
}

/**
 * Reads Table tab rendering toggles from `visualization`, falling back to Jepto defaults.
 */
export function renderingPresentationFromVisualization(
	visualization: unknown
): TableRenderingPresentation {
	const rendering = renderingRecord(visualization);
	const defaultStrictHeights =
		JeptoTableCardDefaults.rendering.rows.strictHeights === true;
	const defaultResizingEnabled =
		JeptoTableCardDefaults.rendering.columns.resizing.enabled === true;

	const rows = rendering['rows'];
	let wrapCellText = !defaultStrictHeights;
	if (rows && typeof rows === 'object' && !Array.isArray(rows)) {
		const strictHeights = (rows as Record<string, unknown>)[
			'strictHeights'
		];
		if (typeof strictHeights === 'boolean') {
			wrapCellText = !strictHeights;
		}
	}

	const columns = rendering['columns'];
	let columnResizingEnabled = defaultResizingEnabled;
	if (columns && typeof columns === 'object' && !Array.isArray(columns)) {
		const resizing = (columns as Record<string, unknown>)['resizing'];
		if (
			resizing &&
			typeof resizing === 'object' &&
			!Array.isArray(resizing)
		) {
			const enabled = (resizing as Record<string, unknown>)['enabled'];
			if (typeof enabled === 'boolean') {
				columnResizingEnabled = enabled;
			}
		}
	}

	return { wrapCellText, columnResizingEnabled };
}

/**
 * Deep-merges Table tab rendering toggles into `visualization.rendering`.
 */
export function mergeTableRenderingIntoVisualization(
	visualization: unknown,
	presentation: Partial<TableRenderingPresentation>
): Record<string, unknown> {
	const base = visualizationRecord(visualization);
	const renderingPatch: Record<string, unknown> = {};

	if (presentation.wrapCellText !== undefined) {
		renderingPatch['rows'] = {
			strictHeights: !presentation.wrapCellText,
		};
	}

	if (presentation.columnResizingEnabled !== undefined) {
		renderingPatch['columns'] = {
			resizing: { enabled: presentation.columnResizingEnabled },
		};
	}

	if (Object.keys(renderingPatch).length === 0) {
		return base;
	}

	base['rendering'] = deepMergeRecord(
		renderingRecord(visualization),
		renderingPatch
	);
	return base;
}

/** Jepto defaults for Table tab behavior toggles (`features` + mirrored viz keys). */
export function defaultTableTabFeatures(): TableFeaturesConfig {
	return {
		pagination: {
			enabled: JeptoTableCardDefaults.pagination.enabled,
			pageSize: JeptoTableCardDefaults.pagination.pageSize,
		},
		sorting: {
			enabled: JeptoTableCardDefaults.columnDefaults.sorting.enabled,
		},
	};
}

/**
 * Visualization patch that removes Table tab overrides while preserving
 * column widths and other non-tab keys (uses null-delete merge).
 */
export function buildTableTabResetVisualizationPatch(): Record<
	string,
	unknown
> {
	return {
		pagination: null,
		sorting: null,
		rendering: null,
	};
}

/**
 * True only for the exact Table tab Reset-to-Defaults visualization shape.
 * Avoids treating incidental form `null` values as null-delete merges.
 */
export function isTableTabResetVisualizationPatch(
	incoming: Record<string, unknown>
): boolean {
	const reset = buildTableTabResetVisualizationPatch();
	const resetKeys = Object.keys(reset);
	const incomingKeys = Object.keys(incoming);
	if (incomingKeys.length !== resetKeys.length) {
		return false;
	}
	return resetKeys.every(
		key =>
			Object.prototype.hasOwnProperty.call(incoming, key) &&
			incoming[key] === null
	);
}

/**
 * Hydrates editor `features` from persisted `visualization` when opening a table card.
 */
export function normalizeTableCardConfigForEditor(
	config: TableCardConfig
): TableCardConfig {
	const fromViz = featuresFromVisualization(config.visualization);
	const hasFeatures =
		config.features !== undefined &&
		Object.keys(config.features).length > 0;

	const features: TableFeaturesConfig = hasFeatures
		? {
				...fromViz,
				...config.features,
				pagination: {
					...fromViz.pagination,
					...config.features?.pagination,
				},
				sorting: {
					...fromViz.sorting,
					...config.features?.sorting,
				},
			}
		: fromViz;

	const visualization = mergeTableFeaturesIntoVisualization(
		config.visualization,
		features
	);

	return {
		...config,
		features:
			Object.keys(features).length > 0 ? features : config.features,
		visualization: visualization as TableCardConfig['visualization'],
	};
}
